const WINDOWS_RESERVED = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(\.|$)/i;
const UNSAFE_FILENAME_CHARS = /[<>:"/\\|?*\u0000-\u001f\u007f]/g; // eslint-disable-line no-control-regex

/**
 * Make an untrusted incoming filename safe for local storage. Path separators
 * and reserved characters are removed rather than escaped, and dotfiles are
 * stripped so nothing can be written outside the received-files directory.
 */
export function sanitizeFileName(input: string, fallback = 'file'): string {
  // Take the last path segment *before* stripping characters, otherwise a
  // traversal path would collapse into one long (but harmless) name.
  const segments = input.normalize('NFKC').split(/[\\/]/);
  let name = segments[segments.length - 1] ?? '';

  name = name.replace(UNSAFE_FILENAME_CHARS, '_');
  name = name.replace(/^[.\s]+|[.\s]+$/g, '').trim();
  if (!name || name === '.' || name === '..') name = fallback;
  if (WINDOWS_RESERVED.test(name)) name = `_${name}`;

  if (name.length > 180) {
    const dot = name.lastIndexOf('.');
    const extension = dot > 0 && name.length - dot <= 12 ? name.slice(dot) : '';
    const stem = extension ? name.slice(0, dot) : name;
    name = `${stem.slice(0, 180 - extension.length)}${extension}`;
  }

  return name || fallback;
}

/** Return a name that does not collide with an existing set (case-insensitive). */
export function uniqueFileName(name: string, used: ReadonlySet<string>): string {
  const key = name.toLowerCase();
  if (!used.has(key)) return name;

  const dot = name.lastIndexOf('.');
  const extension = dot > 0 ? name.slice(dot) : '';
  const stem = dot > 0 ? name.slice(0, dot) : name;

  for (let index = 1; index < 10_000; index += 1) {
    const candidate = `${stem} (${index})${extension}`;
    if (!used.has(candidate.toLowerCase())) return candidate;
  }

  return `${stem}-${Date.now()}${extension}`;
}

export function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot <= 0 || dot === name.length - 1) return '';
  return name.slice(dot + 1).toLowerCase();
}

/**
 * Build a safe relative path for folder transfers. Every segment is sanitized
 * and empty or traversal segments are dropped.
 */
export function sanitizeRelativePath(input: string): string {
  return input
    .split(/[\\/]/)
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0 && segment !== '.' && segment !== '..')
    .map((segment) => sanitizeFileName(segment))
    .join('/');
}
